import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from 'api/notifications';
import {
  getNotificationTarget,
  getNotificationBudgetCode,
  resolveOpportunityLocation,
  VIEW_PATH_MAP,
  appendFocusParam
} from 'utils/notificationRoutes';
import { usePushNotifications } from 'hooks/usePushNotifications';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import BellFilled from '@ant-design/icons/BellFilled';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',

  transform: 'none'
};

const formatNotificationDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const OPPORTUNITY_NOTIFICATION_TYPES = new Set([
  'opportunity_created',
  'opportunity_recovered',
  'opportunity_deleted'
]);

const NATIVE_NOTIFICATION_ICON = '/logovrp1.png';

const supportsNativeNotifications = () =>
  typeof window !== 'undefined' &&
  typeof window.Notification !== 'undefined' &&
  typeof window.Notification.requestPermission === 'function';

const isDocumentFocused = () =>
  typeof document !== 'undefined' && typeof document.hasFocus === 'function' && document.hasFocus();

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const navigate = useNavigate();
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const { user, sessionReauthToken } = useAuth();

  const anchorRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationsError, setNotificationsError] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);
  const nativePermissionRequestedRef = useRef(false);
  const shownNativeNotificationsRef = useRef(new Set());
  const unreadCount = notifications.filter((item) => !item.readAt).length;
  const hasNotifications = notifications.length > 0;

  const loadNotifications = useCallback(async () => {
    if (sessionReauthToken) return;
    setLoadingNotifications(true);
    setNotificationsError(null);
    try {
      const list = await getNotifications({ limit: 15 });
      setNotifications(list);
    } catch (err) {
      setNotificationsError(err.message || 'No se pudieron cargar las notificaciones.');
    } finally {
      setLoadingNotifications(false);
    }
  }, [sessionReauthToken]);
  const { pushReady, registerPush } = usePushNotifications({ onMessageReceived: loadNotifications });

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (sessionReauthToken) return undefined;
    const intervalId = setInterval(() => {
      loadNotifications();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [loadNotifications, sessionReauthToken]);

  useEffect(() => {
    if (!supportsNativeNotifications()) return;
    if (!user) return;
    if (Notification.permission !== 'default') return;
    if (nativePermissionRequestedRef.current) return;
    nativePermissionRequestedRef.current = true;
    Notification.requestPermission()
      .then((permission) => {
        if (permission === 'granted') {
          registerPush();
        }
      })
      .catch((error) => console.error('Permiso de notificaciones denegado', error));
  }, [user, registerPush]);

  const handleToggle = () => {
    if (supportsNativeNotifications() && Notification.permission === 'default' && !nativePermissionRequestedRef.current) {
      nativePermissionRequestedRef.current = true;
      Notification.requestPermission()
        .then((permission) => {
          if (permission === 'granted') {
            registerPush();
          }
        })
        .catch((error) => console.error('Permiso de notificaciones denegado', error));
    }
    setOpen((prevOpen) => {
      const nextOpen = !prevOpen;
      if (nextOpen) {
        loadNotifications();
      }
      return nextOpen;
    });
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notificaciones');
  };

  const handleNotificationClick = useCallback(async (notification) => {
    if (!notification) return;
    let targetPath = null;
    let focusNumero = '';
    if (OPPORTUNITY_NOTIFICATION_TYPES.has(notification.type)) {
      try {
        const locationInfo = await resolveOpportunityLocation(notification);
        if (locationInfo) {
          if (locationInfo.view === 'not-found') {
            window.alert(locationInfo.message || 'La oportunidad ya no existe en la base de datos.');
            return;
          }
          const viewKey = (locationInfo.view || 'oportunidades').toLowerCase();
          targetPath = VIEW_PATH_MAP[viewKey] || VIEW_PATH_MAP.oportunidades;
          focusNumero = locationInfo.highlightNumero || '';
        }
      } catch (err) {
        console.error('Error resolving opportunity location', err);
      }
    }
    if (!targetPath) {
      targetPath = getNotificationTarget(notification);
    }
    if (!notification.readAt && notification.id) {
      try {
        const updated = await markNotificationRead(notification.id);
        if (updated) {
          setNotifications((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        }
      } catch (err) {
        setNotificationsError(err.message || 'No se pudo actualizar la notificación.');
      }
    }
    if (!targetPath) return;
    const finalPath =
      focusNumero && focusNumero.length
        ? appendFocusParam(targetPath, `focusNumero=${encodeURIComponent(focusNumero)}`)
        : targetPath;
    setOpen(false);
    navigate(finalPath);
  }, [navigate]);

  useEffect(() => {
    if (!supportsNativeNotifications()) return;
    if (Notification.permission !== 'granted') return;
    if (pushReady) return;
    if (notifications.length === 0) return;
    notifications.forEach((item) => {
      if (!item?.id || item.readAt || shownNativeNotificationsRef.current.has(item.id)) return;
      try {
        const notificationTitle = item.title || 'Nueva notificación';
        const notificationBody = item.message ? String(item.message).trim() : '';
        const nativeNotification = new Notification(notificationTitle, {
          body: notificationBody,
          icon: NATIVE_NOTIFICATION_ICON,
          tag: `vrp-notification-${item.id}`
        });
        const timeoutId = setTimeout(() => nativeNotification.close(), 5500);
        nativeNotification.onclick = (event) => {
          event.preventDefault();
          if (typeof window !== 'undefined' && typeof window.focus === 'function') {
            window.focus();
          }
          void handleNotificationClick(item);
          clearTimeout(timeoutId);
        };
        nativeNotification.onclose = () => clearTimeout(timeoutId);
        shownNativeNotificationsRef.current.add(item.id);
      } catch (error) {
        console.error('Error mostrando la notificación nativa', error);
      }
    });
  }, [notifications, handleNotificationClick]);

  const markAllAsRead = async () => {
    if (!hasNotifications || unreadCount === 0) return;
    setMarkingAll(true);
    try {
      await markAllNotificationsRead();
      await loadNotifications();
    } catch (err) {
      setNotificationsError(err.message || 'No se pudieron marcar como leídas.');
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount || null} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notification"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {hasNotifications && unreadCount > 0 && (
                        <Tooltip title="Marcar todas como leídas">
                          <IconButton color="success" size="small" onClick={markAllAsRead} disabled={markingAll}>
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                <Box
                  sx={{
                    maxHeight: (theme) => `calc(${theme.spacing(10)} * 6)`,
                    overflowY: 'auto',
                    mb: 1,
                    px: 0
                  }}
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        px: 2,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {notificationsError ? (
                      <ListItem sx={{ py: 1.5 }}>
                        <Alert severity="error" sx={{ width: '100%' }}>
                          {notificationsError}
                        </Alert>
                      </ListItem>
                    ) : null}
                    {!hasNotifications && !loadingNotifications ? (
                      <ListItem sx={{ py: 3, flexDirection: 'column', textAlign: 'center' }}>
                        <Avatar
                          variant="rounded"
                          sx={{
                            mb: 1.5,
                            width: 56,
                            height: 56,
                            bgcolor: 'primary.lighter',
                            color: 'primary.main'
                          }}
                        >
                          <BellFilled />
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Sin notificaciones por ahora
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cuando existan novedades que requieran tu atención, aparecerán aquí.
                        </Typography>
                      </ListItem>
                    ) : null}
                    {hasNotifications &&
                      notifications.map((item) => {
                        const initials =
                          item.sourceUserName && item.sourceUserName.trim()
                            ? item.sourceUserName
                                .split(' ')
                                .slice(0, 2)
                                .map((word) => word[0])
                                .join('')
                                .toUpperCase()
                            : null;
                        const budgetCode = getNotificationBudgetCode(item);
                        const displayTitle =
                          item.type === 'obra_comment' && budgetCode
                            ? `Nueva nota en ${budgetCode}`
                            : item.title;
                        return (
                          <ListItem
                            key={item.id}
                            component={ListItemButton}
                            divider
                            selected={!item.readAt}
                            onClick={() => handleNotificationClick(item)}
                            secondaryAction={
                              <Typography variant="caption" noWrap>
                                {formatNotificationDate(item.createdAt)}
                              </Typography>
                            }
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ color: 'primary.main', bgcolor: 'primary.lighter' }}>
                                {initials || <BellFilled />}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle1" sx={{ fontWeight: item.readAt ? 500 : 700 }}>
                                  {displayTitle}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere'
                                  }}
                                >
                                  {item.message}
                                </Typography>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    {loadingNotifications && (
                      <ListItem sx={{ justifyContent: 'center', py: 1.5 }}>
                        <CircularProgress size={20} />
                      </ListItem>
                    )}
                  </List>
                </Box>
                <Box
                  sx={{
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    py: 0.5,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <Button
                    variant="text"
                    color="primary"
                    onClick={handleViewAll}
                    sx={{ fontWeight: 700, textTransform: 'none' }}
                  >
                    Ver Todo
                  </Button>
                </Box>
              </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
